/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ImageBox.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Box from "@mui/material/Box";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { GetSingleImages } from "../../../redux/actions/actionHandler";

const imageBlobCache = new Map();
const inFlightRequests = new Map();

function ImageBox({ boxes, sx = {} }) {
  const firstImage = boxes?.vehicle_class_details?.[0]?.image;
  const [image, setImage] = useState(
    () => (firstImage && imageBlobCache.get(firstImage)) || "",
  );

  useEffect(() => {
    let isMounted = true;

    if (!firstImage) {
      setImage("");
      return;
    }

    if (imageBlobCache.has(firstImage)) {
      setImage(imageBlobCache.get(firstImage));
      return;
    }

    const fetchImage = async () => {
      try {
        if (!inFlightRequests.has(firstImage)) {
          inFlightRequests.set(
            firstImage,
            GetSingleImages({
              image: firstImage,
              ignore_app_loading_spinner: true,
            }),
          );
        }
        const resp = await inFlightRequests.get(firstImage);
        inFlightRequests.delete(firstImage);
        if (resp) {
          imageBlobCache.set(firstImage, resp);
        }
        if (isMounted) {
          setImage(resp || "");
        }
      } catch (error) {
        inFlightRequests.delete(firstImage);
        console.error("Error fetching image:", error);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [firstImage]);

  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: 1,
        overflow: "hidden",
        ...sx,
      }}
    >
      <img
        alt=""
        src={image}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  );
}

ImageBox.propTypes = {
  boxes: PropTypes.shape({
    vehicle_class_details: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.string,
      }),
    ),
  }),
  sx: PropTypes.object,
};

export default ImageBox;
